
import React, { useState, useEffect } from 'react'
import Modal from '../Modal'
import CustomTableData from '../Table/TableData'
import CustomTableRow from '../Table/TableRow'
import CustomTableBody from '../Table/TableBody'
import CustomTable from '../Table'
import CustomTableHead from '../Table/TableHead'
import TablePagination from '../Table/Pagination'
import Empty from '../../assets/icons/Empty'
import DefaultButton from '../Button/Button'
import {
  PaymentHistory,
} from '../../interfaces/resident'


interface ResidentDTO {
  resident?: {
    address?: string
  }
}

interface PaymentHistoryModalProps {
  open: boolean
  close: () => void
  resident?: ResidentDTO | null
  address?: string
  properties: PaymentHistory[]
}

const ITEMS_PER_PAGE = 6

const formatDate = (date?: string) => {
  if (!date) return 'N/A'

  const parsedDate = new Date(date)

  if (Number.isNaN(parsedDate.getTime())) {
    return 'N/A'
  }

  return parsedDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const PaymentHistoryModal: React.FC<PaymentHistoryModalProps> = ({
  open,
  close,
  address,
  resident,
  properties,
}) => {
  const [currentPage, setCurrentPage] = useState(1)

  // reset page when modal opens
  useEffect(() => {
    if (open) setCurrentPage(1)
  }, [open])


  const tableHeadData = ['Date', 'Levy Period', 'Amount', 'Status']

  const totalPages = Math.ceil(properties.length / ITEMS_PER_PAGE)

  const paginatedData = properties.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  return (
    <Modal
      open={open}
      close={close}
      showClose={false}
      widthClass="w-[calc(100%-16px)] sm:w-[calc(100%-32px)] lg:w-[1040px]"
      modalClassName="p-3 sm:p-4 lg:p-3"
    >
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <h2 className="text-base font-semibold text-[#212529]">Payment History</h2>
          <p className="text-xs font-medium text-[#495057] sm:text-sm">
            {address || 'N/A'}
          </p>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close payment history"
          className="-mt-1 text-xl leading-none text-[#ADB5BD] transition-colors hover:text-[#495057]"
        >
          ×
        </button>
      </header>

      <div className="mt-7 overflow-x-auto bg-[#F8F9FA]">
        <CustomTable className="min-w-[620px]">
          <CustomTableHead
            dataList={tableHeadData}
            cellClassName="!text-left !px-2 first:!pl-5 last:!pl-5"
          />

          {properties.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={tableHeadData.length}>
                  <div className="flex flex-col items-center justify-center h-[300px] gap-4">
                    <Empty />
                    <p className="text-sm font-semibold">No results found</p>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <CustomTableBody>
              {paginatedData.map((item, index) => (
                <CustomTableRow
                  key={item.id}
                  className={index % 2 === 0 ? 'bg-white' : 'bg-[#F8F9FA]'}
                >
                  <CustomTableData dataLabel="Date" text={formatDate(item.date)} />

                  <CustomTableData
                    dataLabel="Levy Period"
                    text={item.levyPeriod}
                  />

                  <CustomTableData
                    dataLabel="Amount"
                    text={`₦ ${item.amount.toLocaleString()}`}
                  />

                  <CustomTableData
                    dataLabel="Status"
                    text={item.status}
                    className={item.status === 'Paid' ? 'text-green-600' : 'text-red-600'}
                  />
                </CustomTableRow>
              ))}
            </CustomTableBody>
          )}
        </CustomTable>
      </div>

      <div className="mt-1">
        {properties.length > ITEMS_PER_PAGE && (
          <TablePagination
            totalPages={totalPages}
            currPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>

      <div className="mt-3 flex justify-end border-t pt-3">
        <DefaultButton buttonType="secondary" onClick={close} size="sm" className="mt-4">
          Close
        </DefaultButton>
      </div>
    </Modal>
  )
}

export default PaymentHistoryModal
